import { isPreOrderActive } from '@cart-services/utils/findPreOrderProduct'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type ECommerceProduct } from '@hooks-api/types/dataModel/products'
import { Box } from '@pets-ui/Box/Box'
import { Chip } from '@pets-ui/Chip/Chip'
import { HStack } from '@pets-ui/HStack/HStack'
import { NumberInput, type NumberInputProps } from '@pets-ui/NumberInput/NumberInput'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { formatPrice } from '@utils'
import { useMemo } from 'react'
import { useContextCouponProductSelector } from './CouponProductSelectorProvider'

export type OnQuantityChange = (product: ECommerceProduct, quantity?: number) => void

export interface CouponProductSelectorItemVariationListProductItemProps {
  product: ECommerceProduct
}

export const CouponProductSelectorItemVariationListProductItem = (props: CouponProductSelectorItemVariationListProductItemProps) => {
  const { product } = props
  const { cartItems, currency, onQuantityChange, isShowOriginalPrice: isShowOriginalPriceProp } = useContextCouponProductSelector()

  const stock = Math.max(0, Number(product.stock))
  const isOutOfStock = stock === 0
  const currentQty = useMemo(() =>
    cartItems.find(
      item => item.cartItemId === product.product_id,
    )?.quantity || 0, [cartItems, product.product_id])
  const remainingQty = Math.max(0, Number(product.stock) - currentQty)
  const isShowOriginalPrice = isShowOriginalPriceProp && product.original_price && product.sale_price && product.original_price > product.sale_price

  const handleQuantityChange: NumberInputProps['onChange'] = useCallbackEvent((quantity) => {
    onQuantityChange?.(product, quantity)
  })

  return (
    <HStack px={2} py={3} alignItems="center" justifyContent="space-between" backgroundColor={colors.grey50} gap={2}>
      <HStack gap={1} alignItems="center" flexWrap="nowrap">
        <Text variant="subtitle" noWrap>
          {product.display_name || product.title}
        </Text>
        {isPreOrderActive(product.pre_order_info) && (
          <Chip variant="square" size="small" color="red" isOutlined isSoft flexShrink={0}>
            預購
          </Chip>
        )}
      </HStack>
      <HStack flex={1} gap={2} alignItems="center" flexWrap="nowrap" justifyContent="flex-end">
        <VStack alignItems="flex-end">
          <HStack gap={1} justifyContent="flex-end">
            {isShowOriginalPrice && (
              <Text color={colors.grey500} textDecoration="line-through">
                {formatPrice(currency, Number(product.original_price))}
              </Text>
            )}
            <Text color={colors.primary600}>{formatPrice(currency, Number(product.sale_price))}</Text>
          </HStack>
          {!isOutOfStock && remainingQty <= 25 && (
            <Text color={colors.green400} fontWeight={400} variant="caption">
              剩餘數量:
              {remainingQty}
            </Text>
          )}
        </VStack>
        <Box display="flex" alignItems="center" justifyContent="center">
          {isOutOfStock
            ? <Text color={colors.red600}>已售完</Text>
            : (
              <NumberInput
                value={currentQty}
                max={stock}
                onChange={handleQuantityChange}
                autoSelect
              />
            )}
        </Box>
      </HStack>
    </HStack>
  )
}
